import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { ArrowUpRight } from "lucide-react";
import { LOGIN } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { optimizeImage } from "@/lib/img";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="hidden lg:block relative bg-[var(--lh-black)]">
        <img src={optimizeImage("https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/8rehssds_ChatGPT%20Image%20Jun%2022%2C%202026%2C%2011_09_16%20PM.png", { w: 1000, name: "localizeher-team-portal" })} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
        <div className="absolute top-10 left-10"><Logo light /></div>
        <div className="absolute bottom-12 left-10 right-10 text-white">
          <div className="overline text-[var(--lh-mauve)] mb-4">Team only</div>
          <p className="font-serif-display text-4xl leading-tight">
            Start showing up<br /><em>on purpose.</em>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Logo /></div>
          <div className="overline text-[var(--lh-mauve)] mb-4">Team sign-in</div>
          <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">Welcome back.</h1>
          <p className="text-[var(--lh-text)] font-light mt-3">
            Memberships launch this <strong>Summer</strong>. Public sign-ups are paused —
            this page is for the LocalizeHER team only.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-7">
            <div>
              <div className="overline text-[var(--lh-muted)] mb-1">Email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-line"
                placeholder="you@yourbiz.com"
                data-testid={LOGIN.emailInput}
              />
            </div>
            <div>
              <div className="overline text-[var(--lh-muted)] mb-1">Password</div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-line"
                placeholder="••••••••"
                data-testid={LOGIN.passwordInput}
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="login-error-message">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid={LOGIN.submitButton}>
              {busy ? "Signing in…" : "Sign in"} <ArrowUpRight size={16} />
            </button>
          </form>

          <p className="mt-10 text-sm text-[var(--lh-muted)]">
            Want to be first when memberships open?{" "}
            <a
              href="/"
              className="text-[var(--lh-mauve)] hover:underline"
              data-testid={LOGIN.registerLink}
            >
              Reserve Your VIP Spot →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
